import { useCallback, useEffect, useState } from "react";
import { apiGet, apiPost, apiPut } from "../lib/api";
import { useScope } from "../scope/ScopeContext";
import { useAuth } from "../auth/AuthContext";
import type {
  OsmListingRow,
  OsmListResponse,
  OsmRefreshResult,
  OsmStatus,
  MapboxStatus,
  OsmApplyDryRun,
  OsmApplyCommitted,
} from "../lib/types";

/** Color classes for an OSM status badge. */
function osmBadge(s: OsmStatus): string {
  switch (s) {
    case "present":
      return "bg-emerald-50 text-emerald-700 ring-emerald-200";
    case "diverged":
      return "bg-amber-50 text-amber-700 ring-amber-200";
    case "unclaimed":
      return "bg-rose-50 text-rose-700 ring-rose-200";
    default:
      return "bg-slate-50 text-slate-500 ring-slate-200";
  }
}

/**
 * The Mapbox column is a SOFT secondary signal: whether the free-tier geocoding
 * API resolves the name to a POI. Its index is POI-sparse, so "not in geocoding"
 * does NOT mean "not on the map" (OSM is the authoritative source). Never render
 * it as an error state.
 */
function mapboxBadge(s: MapboxStatus): string {
  switch (s) {
    case "present":
      return "bg-emerald-50 text-emerald-700 ring-emerald-200";
    default:
      return "bg-slate-50 text-slate-500 ring-slate-200";
  }
}

const mapboxLabel: Record<string, string> = {
  present: "In geocoding",
  missing: "Not in geocoding",
  unknown: "—",
};

const osmLabel: Record<string, string> = {
  present: "In OSM",
  diverged: "OSM differs",
  unclaimed: "Not in OSM",
};

function Badge({ label, cls }: { label: string; cls: string }) {
  return (
    <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium uppercase ring-1 ring-inset ${cls}`}>
      {label}
    </span>
  );
}

function fmtDate(s: string | null): string {
  return s ? new Date(s).toLocaleString() : "—";
}

/** A single editable NAP row for the "Edit NAP" dialog. */
function NapForm({
  row,
  onSave,
  onCancel,
  saving,
}: {
  row: OsmListingRow;
  onSave: (values: {
    name: string | null;
    phone: string | null;
    website: string | null;
    hours: string | null;
    address: string | null;
    lat: number | null;
    lng: number | null;
  }) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const [name, setName] = useState(row.canonicalName ?? "");
  const [phone, setPhone] = useState(row.canonicalPhone ?? "");
  const [website, setWebsite] = useState(row.canonicalWebsite ?? "");
  const [hours, setHours] = useState(row.canonicalHours ?? "");
  const [address, setAddress] = useState(row.canonicalAddress ?? "");
  const [lat, setLat] = useState(row.lat != null ? String(row.lat) : "");
  const [lng, setLng] = useState(row.lng != null ? String(row.lng) : "");

  const submit = () => {
    onSave({
      name: name.trim() || null,
      phone: phone.trim() || null,
      website: website.trim() || null,
      hours: hours.trim() || null,
      address: address.trim() || null,
      lat: lat.trim() === "" ? null : Number(lat),
      lng: lng.trim() === "" ? null : Number(lng),
    });
  };

  const field = "mt-1 block w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-900 shadow-inner focus:border-brand-500 focus:outline-none";
  const label = "block text-[10px] font-semibold uppercase tracking-wide text-slate-500";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/30 p-4">
      <div className="w-full max-w-lg rounded-xl border border-slate-200 bg-white p-5 shadow-lg">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold text-slate-900">Edit NAP — {row.displayName}</h2>
          <button onClick={onCancel} className="text-sm text-slate-400 hover:text-slate-600">✕</button>
        </div>
        <p className="mb-4 text-xs text-slate-500">
          This is the name/phone/website we compare against OSM &amp; Mapbox. Empty fields fall back to the
          Google Places auto-pull; fill them to override.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className={label}>Name</label>
            <input className={field} value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <label className={label}>Phone</label>
            <input className={field} value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
          <div>
            <label className={label}>Website</label>
            <input className={field} value={website} onChange={(e) => setWebsite(e.target.value)} />
          </div>
          <div className="col-span-2">
            <label className={label}>Address</label>
            <input className={field} value={address} onChange={(e) => setAddress(e.target.value)} />
          </div>
          <div className="col-span-2">
            <label className={label}>Hours (free text)</label>
            <input className={field} value={hours} onChange={(e) => setHours(e.target.value)} />
          </div>
          <div>
            <label className={label}>Lat</label>
            <input className={field} value={lat} onChange={(e) => setLat(e.target.value)} />
          </div>
          <div>
            <label className={label}>Lng</label>
            <input className={field} value={lng} onChange={(e) => setLng(e.target.value)} />
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={saving}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-40"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function OsmListingsPage() {
  const { user } = useAuth();
  const { focusedDealerId } = useScope();
  const canWrite = user?.role === "super_admin" || user?.role === "group_admin" || user?.role === "store_admin";

  const [rows, setRows] = useState<OsmListingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [editRow, setEditRow] = useState<OsmListingRow | null>(null);
  const [applyPreview, setApplyPreview] = useState<{ row: OsmListingRow; dry: OsmApplyDryRun } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiGet<OsmListResponse>("/api/osm");
      setRows(res.listings);
      setError(null);
    } catch (err) {
      console.error("osm load failed:", err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, focusedDealerId]);

  const refreshOne = async (r: OsmListingRow) => {
    setBusyId(r.locationId);
    try {
      await apiPost<OsmRefreshResult>(`/api/osm/${r.locationId}/refresh`);
      await load();
    } catch (err) {
      alert(`Check failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusyId(null);
    }
  };

  const saveNap = async (
    r: OsmListingRow,
    values: {
      name: string | null;
      phone: string | null;
      website: string | null;
      hours: string | null;
      address: string | null;
      lat: number | null;
      lng: number | null;
    }
  ) => {
    setBusy(true);
    try {
      await apiPut(`/api/osm/${r.locationId}/nap`, values);
      setEditRow(null);
      await load();
    } catch (err) {
      alert(`Save failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
    }
  };

  const toggleAccept = async (r: OsmListingRow) => {
    if (!r.osmEntityId) return;
    const next = !r.osmAccepted;
    setBusyId(r.locationId);
    try {
      await apiPut(`/api/osm/${r.locationId}/accept`, { accepted: next });
      await load();
    } catch (err) {
      alert(`Accept failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusyId(null);
    }
  };

  const previewApply = async (r: OsmListingRow) => {
    setBusyId(r.locationId);
    try {
      const dry = await apiPost<OsmApplyDryRun>(`/api/osm/${r.locationId}/apply-to-osm`, { dryRun: true });
      setApplyPreview({ row: r, dry });
    } catch (err) {
      alert(`Preview failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusyId(null);
    }
  };

  const commitApply = async (r: OsmListingRow) => {
    setBusy(true);
    try {
      const res = await apiPost<OsmApplyCommitted>(`/api/osm/${r.locationId}/apply-to-osm`, { dryRun: false });
      setApplyPreview(null);
      alert(
        res.action === "create"
          ? "OSM node created. OSM moderators may still review it — re-run a check in ~a day."
          : `OSM ${res.entityId ? `entity ${res.entityId} updated` : "updated"}.`
      );
      await load();
    } catch (err) {
      alert(`Commit failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="px-8 py-8">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">OSM / Mapbox listings</h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-500">
            Per-rooftop map-presence tracking. <span className="font-medium">OSM</span> is the writable,
            authoritative source — ChatGPT’s “near me” maps are OSM-derived, so getting the NAP into OSM is
            the fix. The <span className="font-medium">Mapbox</span> column is a soft secondary signal only
            (whether Mapbox’s geocoding API resolves the name); its free index is POI-sparse, so
            “Not in geocoding” ≠ “not on the map”. “Apply to OSM” writes a public, moderated OSM edit —
            review the preview first.
          </p>
        </div>
        {canWrite && (
          <button
            onClick={() => void load()}
            disabled={busy}
            className="rounded-lg border border-brand-200 bg-brand-50 px-3 py-2 text-sm font-medium text-brand-700 hover:bg-brand-100 disabled:opacity-40"
          >
            Refresh all
          </button>
        )}
      </div>

      {error && (
        <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
      )}

      <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="px-3 py-2">Dealer</th>
              <th className="px-3 py-2">Rooftop</th>
              <th className="px-3 py-2">NAP</th>
              <th className="px-3 py-2">OSM</th>
              <th
                className="px-3 py-2"
                title="Soft signal: does Mapbox’s geocoding API resolve this name to a POI? The free index is POI-sparse — absence here is NOT a claim that the dealer is missing from the map. OSM is the authoritative column."
              >
                Mapbox geocoding
              </th>
              <th className="px-3 py-2">Last checked</th>
              <th className="px-3 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-500">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-500">
                No rooftops in scope.
              </td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.locationId} className="hover:bg-slate-50">
                <td className="px-3 py-2 text-slate-600">{r.dealerName}</td>
                <td className="px-3 py-2">
                  <div className="font-medium text-slate-800">{r.displayName}</div>
                  {r.gbpAddress && <div className="text-xs text-slate-400">{r.gbpAddress}</div>}
                </td>
                <td className="px-3 py-2 text-xs text-slate-500">
                  {[r.canonicalPhone, r.canonicalWebsite].filter(Boolean).join(" · ") || "—"}
                  {r.canonicalPhone === null && r.canonicalWebsite === null && !r.hasListing && (
                    <div className="text-[10px] text-amber-600">NAP not pulled</div>
                  )}
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1.5">
                    <Badge label={r.osmStatus ? osmLabel[r.osmStatus] : "unchecked"} cls={osmBadge(r.osmStatus)} />
                    {r.osmAccepted && (
                      <Badge label="accepted" cls="bg-indigo-50 text-indigo-700 ring-indigo-200" />
                    )}
                  </div>
                  {(r.osmMapUrl || r.osmEditUrl) && (
                    <div className="mt-1 space-x-2 text-[10px]">
                      {r.osmMapUrl && (
                        <a href={r.osmMapUrl} target="_blank" rel="noreferrer" className="text-brand-700 hover:underline">map</a>
                      )}
                      {r.osmEditUrl && (
                        <a href={r.osmEditUrl} target="_blank" rel="noreferrer" className="text-brand-700 hover:underline">edit</a>
                      )}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2">
                  <Badge label={mapboxLabel[r.mapboxStatus] ?? r.mapboxStatus} cls={mapboxBadge(r.mapboxStatus)} />
                </td>
                <td className="px-3 py-2 text-xs text-slate-500">
                  {fmtDate(r.lastCheckedAt)}
                  {r.lastCheckedBy && <div className="text-[10px] text-slate-400">{r.lastCheckedBy}</div>}
                  {r.lastError && <div className="mt-0.5 max-w-[16rem] text-[10px] text-rose-600" title={r.lastError}>{r.lastError}</div>}
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="flex justify-end gap-1.5">
                    <button
                      onClick={() => void refreshOne(r)}
                      disabled={busyId === r.locationId}
                      className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                      title="Re-check OSM + Mapbox now"
                    >
                      Check
                    </button>
                    {canWrite && (
                      <>
                        <button
                          onClick={() => setEditRow(r)}
                          className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
                        >
                          NAP
                        </button>
                        <button
                          onClick={() => void previewApply(r)}
                          disabled={busyId === r.locationId || !r.hasListing}
                          className="rounded-lg border border-brand-200 bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-700 hover:bg-brand-100 disabled:cursor-not-allowed disabled:opacity-40"
                          title={r.hasListing ? "Plan the OSM write (dry run first)" : "Run a check first to populate NAP"}
                        >
                          Apply to OSM
                        </button>
                        <button
                          onClick={() => void toggleAccept(r)}
                          disabled={busyId === r.locationId || !r.osmEntityId}
                          className={`rounded-lg border px-2.5 py-1 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-40 ${
                            r.osmAccepted
                              ? "border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                              : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                          }`}
                          title={
                            r.osmEntityId
                              ? r.osmAccepted
                                ? "Human-accepted — the poller keeps this present. Click to release and resume NAP-based classification."
                                : "Mark this entity as confirmed-correct for this rooftop (e.g. same-building). The poller will keep it present while the entity exists."
                              : "Run a check first to find the OSM entity"
                          }
                        >
                          {r.osmAccepted ? "Accepted" : "Accept"}
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editRow && (
        <NapForm
          row={editRow}
          saving={busy}
          onCancel={() => setEditRow(null)}
          onSave={(values) => void saveNap(editRow, values)}
        />
      )}

      {applyPreview && (
        <ApplyPreviewDialog
          row={applyPreview.row}
          dry={applyPreview.dry}
          busy={busy}
          onCancel={() => setApplyPreview(null)}
          onCommit={() => void commitApply(applyPreview.row)}
        />
      )}
    </div>
  );
}

function ApplyPreviewDialog({
  row,
  dry,
  busy,
  onCancel,
  onCommit,
}: {
  row: OsmListingRow;
  dry: OsmApplyDryRun;
  busy: boolean;
  onCancel: () => void;
  onCommit: () => void;
}) {
  const isUpdate = dry.action === "update";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/30 p-4">
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-xl border border-slate-200 bg-white shadow-lg">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <h2 className="text-base font-semibold text-slate-900">
            Apply to OSM — {row.displayName}
          </h2>
          <button onClick={onCancel} className="text-sm text-slate-400 hover:text-slate-600">✕</button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">
          <div className="mb-3 flex items-center gap-2">
            <Badge
              label={isUpdate ? `update node ${dry.entityId ?? ""}` : "create new node"}
              cls="bg-indigo-50 text-indigo-700 ring-indigo-200"
            />
            <span className="text-xs text-slate-500">
              This is a <span className="font-medium">public, moderated</span> edit to OpenStreetMap.
            </span>
          </div>
          <div className="mb-3">
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">Tags to add / change</div>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] uppercase text-slate-400">
                  <th className="py-1 pr-3">Key</th>
                  <th className="py-1 pr-3">Current</th>
                  <th className="py-1">New</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {Object.entries(dry.addOrUpdate).map(([k, v]) => (
                  <tr key={k}>
                    <td className="py-1 pr-3 font-mono text-slate-600">{k}</td>
                    <td className="py-1 pr-3 text-slate-400">{v.from ?? "(new)"}</td>
                    <td className="py-1 text-slate-800">{v.to}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {Object.keys(dry.preserved).length > 0 && (
            <div className="mb-3">
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                Preserved (not managed by us)
              </div>
              <div className="text-xs text-slate-500">{Object.keys(dry.preserved).join(", ")}</div>
            </div>
          )}
          <details className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
            <summary className="cursor-pointer text-xs font-medium text-slate-600">Raw changeet XML</summary>
            <pre className="mt-2 overflow-x-auto text-[10px] leading-tight text-slate-700">{dry.xml}</pre>
          </details>
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-3">
          <button
            onClick={onCancel}
            disabled={busy}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            onClick={onCommit}
            disabled={busy}
            className="rounded-lg bg-rose-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-40"
          >
            {busy ? "Committing…" : "Commit to OSM"}
          </button>
        </div>
      </div>
    </div>
  );
}
